import { KeyboardEvent } from 'react';

import { CRAFT_ROOT_ID } from 'craft/constants';
import { useEditor, useSelection } from 'craft/hooks';

import { checkConditions } from './checkConditions';
import { useShortcutQueries } from './useShortcutQueries';

export const useNavigationShortcuts = () => {
  const { actions } = useEditor();

  const { selection, nextNodeId, previousNodeId, lastNodeId } = useSelection();
  const { isInFocusMode, isInSelectionMode, noSelection } = useShortcutQueries();

  // TODO(mat) add actual check if node exists before selecting it
  const selectIfNodeExists = (nodeId?: string | null, e?: KeyboardEvent) => {
    if (!nodeId) {
      return;
    }
    if (e?.shiftKey) {
      actions.selectNode([...selection.all, nodeId]);
    } else {
      actions.selectNode(nodeId);
    }
  };

  return {
    keyMap: {
      /** Arrow key navigation */
      nextNodeWithNoSelection: {
        hotkeys: ['right', 'down', 'shift+right', 'shift+down', 'tab'],
        enabled: checkConditions(noSelection),
      },
      nextNodeWithSelection: {
        hotkeys: ['right', 'down', 'shift+right', 'shift+down', 'tab'],
        enabled: checkConditions(isInSelectionMode),
      },
      nextNodeWithMultiSelection: {
        hotkeys: ['shift+right', 'shift+down'],
        enabled: checkConditions(isInSelectionMode),
      },
      previousNodeWithNoSelection: {
        hotkeys: ['left', 'up', 'shift+left', 'shift+up', 'shift+tab'],
        enabled: checkConditions(noSelection),
      },
      previousNodeWithSelection: {
        hotkeys: ['left', 'up', 'shift+tab'],
        enabled: checkConditions(isInSelectionMode),
      },
      previousNodeWithMutiSelection: {
        hotkeys: ['shift+left', 'shift+up'],
        enabled: checkConditions(isInSelectionMode),
      },
      // disable scrolling with arrows
      preventDefault: {
        hotkeys: ['left', 'right', 'up', 'down', 'tab', 'shift+tab'],
        enabled: checkConditions(() => !isInFocusMode()),
      },
    },
    handlers: {
      nextNodeWithNoSelection: () => actions.selectNode(CRAFT_ROOT_ID),
      nextNodeWithSelection: () => selectIfNodeExists(nextNodeId(selection.last)),
      nextNodeWithMultiSelection: () => {
        const nextNode = nextNodeId(selection.last);
        if (nextNode) {
          actions.selectNode([...selection.all, nextNode]);
        }
      },

      previousNodeWithNoSelection: () => selectIfNodeExists(lastNodeId()),
      previousNodeWithSelection: () => selectIfNodeExists(previousNodeId(selection.first)),
      previousNodeWithMutiSelection: () => {
        const previousNode = previousNodeId(selection.first);
        if (previousNode) {
          actions.selectNode([previousNode, ...selection.all]);
        }
      },

      preventDefault: (e: KeyboardEvent) => e.preventDefault(),
    },
  };
};
